// GROUND-M M2 STITCH (pure; client groundtree.js / its decode worker and the
// gate run the SAME code). APP_REARCH §2h.
//
// baseOf: one decoded node -> the arrays every drawn copy of it starts from:
// mm coordinates, heights, the geomorph delta (its height minus the PARENT's
// surface there: the from-heights, computed where the node is decoded), the
// morph tag (morph.js slots), the triangles grouped by quadrant (quads draws),
// and per line (the 4 sides, the 2 inner lines a quadrant draw exposes) the
// vertices on it, sorted.
//
// stitchAll: no skirts, no 2:1 balance — where a drawn node meets a COARSER
// drawn mesh, the fine node's border is re-triangulated onto the coarse one
// (owner: "mesh-level stitch along the border only"): the coarse vertices it
// lacks are inserted (an edge split), its own extra border vertices removed
// (their fan re-triangulated, ear clipping in plan), and every vertex on the
// shared line takes the coarse node's height and delta. The morph slots
// (morph.js slotsOf) give those vertices the coarse node's m, so the shared
// line evaluates identically on both sides at every m: watertight by
// construction (the gate's stitch row checks it edge for edge). A point the
// coarse node must hold — a fine corner on its side, a fine vertex that can't
// go (a hole's or a wall's: locked) — is inserted into the coarse node first
// (its own line, linear there): nodes are processed coarse mesh first.
import { nodeSide, indexNode, groundAt } from './lookup.js';
import { N } from './codec.js';
import { coverOf, footprints, SIDE_D } from './morph.js';

const NOSTAND = 16;
// THE DELTA CODE (owner: i8): one signed byte a vertex, one GLOBAL log scale
// d = sign(c) q (2^(|c| / k) - 1) — 4.4 % steps from 4 mm, |d| to 24.5 m
// (clamped past). Global, so a shared vertex copies the coarse node's CODE
// and both sides decode it bit for bit (watertight at every m); the step
// only moves a vertex's m = 0 target off the parent's surface by <= 2.2 %
// of its delta (the pop row). The vertex shader decodes the same formula.
export const DQ = { q: 0.1, k: 16 };
export function encD(d) {
  if (!(d !== 0 && Number.isFinite(d))) return 0;
  const c = Math.min(127, Math.round(DQ.k * Math.log2(1 + Math.abs(d) / DQ.q)));
  return d < 0 ? -c : c;
}
const f32 = Math.fround;
export function decD(c) {
  if (!c) return 0;
  const v = f32(DQ.q * f32(Math.pow(2, f32(Math.abs(c) / DQ.k)) - 1));
  return c < 0 ? -v : v;
}
// lines: 0 top (z = lo), 1 bottom (z = hi), 2 left (x = lo), 3 right (x = hi),
// 4 the vertical inner line (x = mid), 5 the horizontal one (z = mid)
const HORIZ = [true, true, false, false, false, true];

// mesh: codec meshOf (+ level); parent: the parent's indexNode'd mesh (null:
// a root — delta 0); opt.portal: the grazing-portal fallback — the outline of
// a hole and the cut's walls keep delta 0 (drawn at m = 1) off the border lines
export function baseOf(mesh, k, tx, ty, parent = null, opt = {}) {
  const nv = mesh.x.length, nt = mesh.idx.length / 3, s = 1 << k, side = nodeSide(k);
  const lo = 250, hi = 500 * s * N + 250, mid = 500 * s * (N >> 1) + 250;
  const xm = new Int32Array(nv), zm = new Int32Array(nv), h = new Float64Array(nv), d = new Float32Array(nv), t = new Uint8Array(nv);
  for (let v = 0; v < nv; v++) {
    xm[v] = Math.round(mesh.x[v] * 1000); zm[v] = Math.round(mesh.z[v] * 1000); h[v] = mesh.y[v];
    const L = xm[v] === lo, R = xm[v] === hi, T = zm[v] === lo, B = zm[v] === hi;
    t[v] = (L || R) && (T || B) ? 5 + (R ? 1 : 0) + (B ? 2 : 0) : T ? 1 : B ? 2 : L ? 3 : R ? 4 : 0;
  }
  if (parent) {
    const px0 = (tx & 1) * side, pz0 = (ty & 1) * side;
    for (let v = 0; v < nv; v++) {
      const py = groundAt(parent, mesh.x[v] + px0, mesh.z[v] + pz0);
      d[v] = Number.isFinite(py) ? decD(encD(mesh.y[v] - py)) : 0;
    }
  }
  // open edges (a hole's outline) and the walls' vertices: locked (never
  // removed by a stitch) — and, opt.portal, drawn at their own height
  const cnt = new Map();
  for (let i = 0; i < nt; i++) {
    for (let e = 0; e < 3; e++) {
      const a = mesh.idx[3 * i + e], b = mesh.idx[3 * i + (e + 1) % 3], key = a < b ? a * nv + b : b * nv + a;
      cnt.set(key, (cnt.get(key) || 0) + 1);
    }
  }
  const lock = new Uint8Array(nv);
  const onLine = (a, b) => (xm[a] === xm[b] && (xm[a] === lo || xm[a] === hi)) || (zm[a] === zm[b] && (zm[a] === lo || zm[a] === hi));
  for (const [key, c] of cnt) {
    if (c !== 1) continue;
    const a = Math.floor(key / nv), b = key - a * nv;
    if (onLine(a, b)) continue;
    lock[a] = lock[b] = 1;
  }
  // a wall (NOSTAND, or any triangle of no plan area): its vertices stay
  for (let i = 0; i < nt; i++) {
    const a = mesh.idx[3 * i], b = mesh.idx[3 * i + 1], c = mesh.idx[3 * i + 2];
    const ar = (xm[b] - xm[a]) * (zm[c] - zm[a]) - (zm[b] - zm[a]) * (xm[c] - xm[a]);
    if ((mesh.lab[i] & NOSTAND) || ar === 0) lock[a] = lock[b] = lock[c] = 1;
  }
  // (interior only: a border-line vertex's delta is the line's — a face on
  // the line is one node's, a lock there on one side only would crack)
  if (opt.portal) for (let v = 0; v < nv; v++) if (lock[v] && !t[v]) d[v] = 0;
  // triangles by quadrant (centroid); qok: no triangle spans two
  const midm = mid / 1000;
  const quadOf = new Uint8Array(nt), qn = [0, 0, 0, 0];
  let qok = true;
  for (let i = 0; i < nt; i++) {
    const a = mesh.idx[3 * i], b = mesh.idx[3 * i + 1], c = mesh.idx[3 * i + 2];
    const cx = (mesh.x[a] + mesh.x[b] + mesh.x[c]) / 3, cz = (mesh.z[a] + mesh.z[b] + mesh.z[c]) / 3;
    const q = (cz >= midm ? 2 : 0) + (cx >= midm ? 1 : 0);
    for (const v of [a, b, c]) {
      if ((q & 1 ? xm[v] < mid : xm[v] > mid) || (q & 2 ? zm[v] < mid : zm[v] > mid)) qok = false;
    }
    quadOf[i] = q; qn[q]++;
  }
  const qr = new Int32Array(5);
  for (let q = 0; q < 4; q++) qr[q + 1] = qr[q] + qn[q];
  const idx = new Uint32Array(3 * nt), lab = new Uint8Array(nt), fill = qr.slice(0, 4);
  for (let i = 0; i < nt; i++) {
    const j = fill[quadOf[i]]++;
    idx[3 * j] = mesh.idx[3 * i]; idx[3 * j + 1] = mesh.idx[3 * i + 1]; idx[3 * j + 2] = mesh.idx[3 * i + 2];
    lab[j] = mesh.lab[i];
  }
  // the vertices on each line, sorted along it
  const lines = [[], [], [], [], [], []];
  for (let v = 0; v < nv; v++) {
    if (zm[v] === lo) lines[0].push(v);
    if (zm[v] === hi) lines[1].push(v);
    if (xm[v] === lo) lines[2].push(v);
    if (xm[v] === hi) lines[3].push(v);
    if (xm[v] === mid) lines[4].push(v);
    if (zm[v] === mid) lines[5].push(v);
  }
  for (let l = 0; l < 6; l++) lines[l] = Int32Array.from(lines[l].sort((a, b) => (HORIZ[l] ? xm[a] - xm[b] : zm[a] - zm[b])));
  return { k, tx, ty, nv, nt, xm, zm, h, d, t, idx, lab, qr, qok, lock, lines, lo, hi, mid };
}

// ------------------------------------------------------------ one node's working copy
class Work {
  constructor(B, act) {
    this.B = B;
    this.ox = B.tx * nodeSide(B.k) * 1000; this.oz = B.ty * nodeSide(B.k) * 1000;   // world mm of local 0
    let n = 0;
    for (let q = 0; q < 4; q++) if (!act || act[q]) n += B.qr[q + 1] - B.qr[q];
    this.tri = []; this.lab = [];
    for (let q = 0; q < 4; q++) {
      if (act && !act[q]) continue;
      for (let i = B.qr[q]; i < B.qr[q + 1]; i++) { this.tri.push(B.idx[3 * i], B.idx[3 * i + 1], B.idx[3 * i + 2]); this.lab.push(B.lab[i]); }
    }
    this.alive = new Uint8Array(n).fill(1);
    this.nAlive = n;
    // vertices beyond the base: appended (x / z mm, h, d, t)
    this.xm = Array.from(B.xm); this.zm = Array.from(B.zm); this.h = Array.from(B.h); this.d = Array.from(B.d); this.t = Array.from(B.t);
    this.lock = B.lock;
    this.fail = 0; this.whys = {};
    this.edited = false;
    // incidence of the line vertices (lazy)
    this.inc = null;
    this.lines = null;
  }
  _prep() {
    if (this.inc) return;
    const B = this.B, on = new Uint8Array(B.nv);
    for (const L of B.lines) for (const v of L) on[v] = 1;
    this.inc = new Map();
    for (let i = 0; i < this.tri.length / 3; i++) {
      for (let e = 0; e < 3; e++) {
        const v = this.tri[3 * i + e];
        if (!on[v]) continue;
        (this.inc.get(v) || this.inc.set(v, []).get(v)).push(i);
      }
    }
    // the line vertices a live triangle uses
    this.lines = B.lines.map((L) => Array.from(L).filter((v) => this.inc.has(v)));
  }
  along(l, v) { return HORIZ[l] ? this.ox + this.xm[v] : this.oz + this.zm[v]; }
  lineAt(l) { const B = this.B; return l === 0 || l === 2 ? B.lo : l === 1 || l === 3 ? B.hi : B.mid; }
  // the live vertices on line l within [a, b] (world mm along) -> [{ c, v, h, d }]
  lineVerts(l, a, b) {
    this._prep();
    const out = [];
    for (const v of this.lines[l]) {
      const c = this.along(l, v);
      if (c >= a && c <= b && this.inc.get(v).some((i) => this.alive[i])) out.push({ c, v, h: this.h[v], d: this.d[v] });
    }
    return out;
  }
  // the spans of line l within [a, b] no live edge covers (a hole there:
  // a bore opening on the line) -> [[c0, c1]]
  holeSpans(l, a, b) {
    this._prep();
    const byC = new Map();
    for (const v of this.lines[l]) {
      const c = this.along(l, v);
      if (c < a || c > b || !this.inc.get(v).some((i) => this.alive[i])) continue;
      (byC.get(c) || byC.set(c, []).get(c)).push(v);
    }
    const cs = [...byC.keys()].sort((p, q) => p - q), out = [];
    for (let k = 0; k + 1 < cs.length; k++) {
      const his = new Set(byC.get(cs[k + 1]));
      const edge = byC.get(cs[k]).some((v) => this.inc.get(v).some((i) => this.alive[i] && [this.tri[3 * i], this.tri[3 * i + 1], this.tri[3 * i + 2]].some((u) => his.has(u))));
      if (!edge) out.push([cs[k], cs[k + 1]]);
    }
    return out;
  }
  _addTri(a, b, c, lab) {
    const i = this.tri.length / 3;
    this.tri.push(a, b, c); this.lab.push(lab);
    if (i >= this.alive.length) { const na = new Uint8Array(Math.max(16, this.alive.length * 2)); na.set(this.alive); this.alive = na; }
    this.alive[i] = 1; this.nAlive++;
    for (const v of [a, b, c]) { const L = this.inc.get(v); if (L) L.push(i); }
    return i;
  }
  _why(k, n = 1, info = null) {
    this.fail += n; this.whys[k] = (this.whys[k] || 0) + n;
    if (typeof process !== 'undefined' && process.env && process.env.GM_DEBUG) console.log('stitch', k, `${this.B.k}_${this.B.tx}_${this.B.ty}`, info);
  }
  _kill(i) { if (this.alive[i]) { this.alive[i] = 0; this.nAlive--; } }
  _newVert(l, c, h, d, tag) {
    const v = this.xm.length, along = c, line = this.lineAt(l);
    if (HORIZ[l]) { this.xm.push(along - this.ox); this.zm.push(line); }
    else { this.xm.push(line); this.zm.push(along - this.oz); }
    this.h.push(h); this.d.push(d); this.t.push(tag);
    this.inc.set(v, []);
    return v;
  }
  // a vertex at along c on line l (the existing one, or one split into the
  // live edge spanning c; h / d null: linear along that edge) -> vertex id or -1
  insertAt(l, c, h = null, d = null) {
    this._prep();
    const L = this.lines[l];
    // the live vertices nearest c on each side (every copy there: a kerb's)
    let cLo = -Infinity, cHi = Infinity;
    const live = (v) => this.inc.get(v).some((i) => this.alive[i]);
    for (const v of L) {
      if (!live(v)) continue;
      const cv = this.along(l, v);
      if (cv === c) return v;
      if (cv < c && cv > cLo) cLo = cv;
      if (cv > c && cv < cHi) cHi = cv;
    }
    if (cLo === -Infinity || cHi === Infinity) { this._why('no_span'); return -1; }
    const los = L.filter((v) => live(v) && this.along(l, v) === cLo), his = new Set(L.filter((v) => live(v) && this.along(l, v) === cHi));
    let lo = -1, hi = -1, ts = [];
    for (const a of los) {
      for (const i of this.inc.get(a)) {
        if (!this.alive[i]) continue;
        const o = [this.tri[3 * i], this.tri[3 * i + 1], this.tri[3 * i + 2]].find((u) => his.has(u));
        if (o === undefined) continue;
        if (lo < 0) { lo = a; hi = o; }
        if (a === lo && o === hi) ts.push(i);
      }
    }
    if (!ts.length) return -2;          // inside a hole on the line: nothing to split
    const ca = this.along(l, lo), cb = this.along(l, hi), f = (c - ca) / (cb - ca);
    if (h === null) { h = this.h[lo] + (this.h[hi] - this.h[lo]) * f; d = decD(encD(this.d[lo] + (this.d[hi] - this.d[lo]) * f)); }
    const v = this._newVert(l, c, h, d, l < 4 ? 1 + l : 0);
    for (const i of ts) {
      // rotate so the split edge is (p0, p1) in triangle order
      let p = [this.tri[3 * i], this.tri[3 * i + 1], this.tri[3 * i + 2]];
      while (!((p[0] === lo && p[1] === hi) || (p[0] === hi && p[1] === lo))) p = [p[1], p[2], p[0]];
      this._kill(i);
      this._addTri(p[0], v, p[2], this.lab[i]);
      this._addTri(v, p[1], p[2], this.lab[i]);
    }
    // keep the line sorted
    const at = L.indexOf(hi);
    L.splice(at, 0, v);
    for (let l2 = 0; l2 < 6; l2++) {
      if (l2 === l) continue;
      // a point on two lines (an inner line meeting a side): list it there too
      const on = HORIZ[l2] ? this.zm[v] === this.lineAt(l2) : this.xm[v] === this.lineAt(l2);
      if (on) { const L2 = this.lines[l2], c2 = this.along(l2, v); let k = 0; while (k < L2.length && this.along(l2, L2[k]) < c2) k++; L2.splice(k, 0, v); }
    }
    this.edited = true;
    return v;
  }
  // remove the run of line-l vertices vs (between the kept u and w): their
  // fans re-triangulated in plan -> false when they can't go (locked)
  removeRun(l, vs) {
    this._prep();
    if (vs.some((v) => v >= this.B.nv ? false : this.lock[v])) { this._why('locked', vs.length, { l, vs, c: vs.map((v) => this.along(l, v)) }); return false; }
    const rm = new Set(vs);
    const tris = new Set();
    for (const v of vs) for (const i of this.inc.get(v)) if (this.alive[i]) tris.add(i);
    if (!tris.size) return true;
    // the boundary of their union (undirected edges used once)
    const ec = new Map();
    let lab = 0;
    for (const i of tris) {
      const a = this.tri[3 * i], b = this.tri[3 * i + 1], c = this.tri[3 * i + 2];
      lab = Math.max(lab, this.lab[i] & ~NOSTAND);
      for (const [p, q] of [[a, b], [b, c], [c, a]]) {
        const key = p < q ? `${p},${q}` : `${q},${p}`;
        ec.set(key, (ec.get(key) || 0) + 1);
      }
    }
    const nb = new Map();
    for (const [key, c] of ec) {
      if (c !== 1) continue;
      const [p, q] = key.split(',').map(Number);
      (nb.get(p) || nb.set(p, []).get(p)).push(q);
      (nb.get(q) || nb.set(q, []).get(q)).push(p);
    }
    // the cycle; then the removed vertices dropped from it
    const start = [...nb.keys()].find((v) => !rm.has(v));
    if (start === undefined || [...nb.values()].some((L) => L.length !== 2)) { this._why('not_disk', vs.length); return false; }
    const cyc = [start];
    let prev = -1, cur = start;
    for (;;) {
      const L = nb.get(cur), nx = L[0] !== prev ? L[0] : L[1];
      if (nx === start) break;
      cyc.push(nx); prev = cur; cur = nx;
      if (cyc.length > nb.size) { this._why('cycle', vs.length); return false; }
    }
    if (cyc.length !== nb.size) { this._why('cycle', vs.length); return false; }
    const poly = cyc.filter((v) => !rm.has(v));
    const out = earClip(poly, this.xm, this.zm);
    if (!out) { this._why('ear', vs.length); return false; }
    for (const i of tris) this._kill(i);
    // the fan is a disk of ground: counter-clockwise seen from above (codec
    // meshOf's convention; the ground is drawn front side only), never the
    // first old triangle's sense (a fold's underside would flip the fan)
    for (const [a, b, c] of out) {
      const ar = (this.xm[b] - this.xm[a]) * (this.zm[c] - this.zm[a]) - (this.zm[b] - this.zm[a]) * (this.xm[c] - this.xm[a]);
      if (ar < 0) this._addTri(a, b, c, lab); else this._addTri(a, c, b, lab);
    }
    for (let l2 = 0; l2 < 6; l2++) this.lines[l2] = this.lines[l2].filter((v) => !rm.has(v));
    this.edited = true;
    return true;
  }
  // -> { pos Float32Array (x, aH, z; node-local m), d Float32Array (decoded),
  //      dc Int8Array (the GPU's code), t Uint8Array, idx Uint32Array, lab }
  out() {
    const nv = this.xm.length, pos = new Float32Array(3 * nv), d = new Float32Array(nv), dc = new Int8Array(nv), t = new Uint8Array(nv);
    for (let v = 0; v < nv; v++) { pos[3 * v] = this.xm[v] / 1000; pos[3 * v + 1] = this.h[v]; pos[3 * v + 2] = this.zm[v] / 1000; d[v] = this.d[v]; dc[v] = encD(this.d[v]); t[v] = this.t[v]; }
    const idx = new Uint32Array(3 * this.nAlive), lab = new Uint8Array(this.nAlive);
    for (let i = 0, j = 0; i < this.tri.length / 3; i++) {
      if (!this.alive[i]) continue;
      idx[3 * j] = this.tri[3 * i]; idx[3 * j + 1] = this.tri[3 * i + 1]; idx[3 * j + 2] = this.tri[3 * i + 2]; lab[j++] = this.lab[i];
    }
    return { pos, d, dc, t, idx, lab, fail: this.fail, whys: this.whys };
  }
}

// ear clipping in plan (mm ints): poly a simple polygon (vertex ids) -> [[a, b, c]] or null
export function earClip(poly, X, Z) {
  const P = poly.slice();
  let area = 0;
  for (let i = 0; i < P.length; i++) { const a = P[i], b = P[(i + 1) % P.length]; area += X[a] * Z[b] - X[b] * Z[a]; }
  if (area < 0) P.reverse();
  const cr = (a, b, c) => (X[b] - X[a]) * (Z[c] - Z[a]) - (Z[b] - Z[a]) * (X[c] - X[a]);
  const out = [];
  let guard = 0;
  while (P.length > 3 && guard++ < 100000) {
    let cut = -1;
    for (let i = 0; i < P.length && cut < 0; i++) {
      const a = P[(i + P.length - 1) % P.length], b = P[i], c = P[(i + 1) % P.length];
      if (cr(a, b, c) <= 0) continue;                      // reflex or flat
      let inside = false;
      for (const p of P) {
        if (p === a || p === b || p === c) continue;
        if (cr(a, b, p) >= 0 && cr(b, c, p) >= 0 && cr(c, a, p) >= 0) { inside = true; break; }
      }
      if (!inside) cut = i;
    }
    if (cut < 0) return null;
    const a = P[(cut + P.length - 1) % P.length], b = P[cut], c = P[(cut + 1) % P.length];
    out.push([a, b, c]);
    P.splice(cut, 1);
  }
  if (P.length === 3) { if (cr(P[0], P[1], P[2]) > 0) out.push([P[0], P[1], P[2]]); else if (cr(P[0], P[1], P[2]) < 0) return null; }
  return out;
}

// the node line holding a footprint side: e (a drawn entry), s its side -> line id
export function lineOf(e, s) {
  const n = e.n, S = 256 << n.k, fS = 256 << e.fk;
  const fx0 = e.fx * fS, fz0 = e.fy * fS, x0 = n.tx * S, z0 = n.ty * S;
  if (s === 0) return fz0 === z0 ? 0 : 5;
  if (s === 1) return fz0 + fS === z0 + S ? 1 : 5;
  if (s === 2) return fx0 === x0 ? 2 : 4;
  return fx0 + fS === x0 + S ? 3 : 4;
}
// a node's line through world mm coordinate `at` (perpendicular to `horiz`)
export function lineThrough(n, horiz, at) {
  const S = nodeSide(n.k) * 1000, o = (horiz ? n.ty : n.tx) * S;
  const lo = o + 250, hi = o + S + 250;
  if (at === lo) return horiz ? 0 : 2;
  if (at === hi) return horiz ? 1 : 3;
  return horiz ? 5 : 4;
}
// the node line holding world mm point (xw, zw) on its boundary or an inner
// line -> [line, along]
function lineFor(n, xw, zw) {
  const S = nodeSide(n.k) * 1000, ox = n.tx * S + 250, oz = n.ty * S + 250;
  if (zw === oz) return [0, xw];
  if (zw === oz + S) return [1, xw];
  if (xw === ox) return [2, zw];
  if (xw === ox + S) return [3, zw];
  if (xw === ox + S / 2) return [4, zw];
  return [5, xw];
}
export const sampMm = (u) => 250 + 500 * u;     // L0 sample index -> world mm

// draw (morph.js drawSet), bases: key -> baseOf, top -> Map node key ->
// { work, sig } ; opt.stitch false: none (the twin). opt.cache (the client,
// frame to frame): Map key -> { work, sig } of the last set, replaced by
// stitchDone — a node whose sig is unchanged keeps its stitched Work as it
// was. The sig is everything its Work is made of: the node's draw form, the
// extras it must hold (in the order they go in), and per side / corner it
// adopts the coarse mesh's vertices and holes THERE (not that mesh's sig: a
// coarse node's change elsewhere on its line re-stitches nothing). A Work is
// a pure function of its sig, so a cached set equals an uncached one byte
// for byte (the gate's stitch-inc row). opt.twinSig: the draw form only
// (the stitch-inc twin: a stale Work kept).
// The client stitches a set across frames: stitchPlan, then stitchStep per
// node (coarse mesh first: a step reads only the Works before it), then
// stitchDone; stitchAll is the three at once.
const hashS = (str) => {
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < str.length; i++) { const c = str.charCodeAt(i); h1 = Math.imul(h1 ^ c, 16777619); h2 = Math.imul(h2 + c, 2246822519) ^ (h2 >>> 13); }
  return (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36);
};
export function stitchAll(draw, bases, top, opt = {}) {
  const P = stitchPlan(draw, bases, top, opt);
  while (stitchStep(P));
  return stitchDone(P);
}
// -> the plan: who adopts what, the extras, the corners, the order; P.i the
// next node, P.works the Works so far
export function stitchPlan(draw, bases, top, opt = {}) {
  const fp = footprints(draw);
  const byNode = new Map();
  for (const e of draw.values()) {
    const key = `${e.n.k}_${e.n.tx}_${e.n.ty}`;
    (byNode.get(key) || byNode.set(key, []).get(key)).push(e);
  }
  const adopt = new Map(), extra = new Map(), local = new Map(), corner = new Map(), acts = new Map();
  for (const [key, es] of byNode) {
    const whole = es.some((e) => e.quad === undefined);
    const act = whole ? null : [0, 1, 2, 3].map((q) => es.some((e) => e.quad === q));
    acts.set(key, act);
    adopt.set(key, []); extra.set(key, []); corner.set(key, []);
    local.set(key, whole ? 'w' : act.map(Number).join(''));
  }
  // (1) from the bases alone: who adopts what, the extras, the corners
  if (opt.stitch !== false) {
    for (const [key, es] of byNode) {
      const B = bases.get(key), ox = B.tx * nodeSide(B.k) * 1000, oz = B.ty * nodeSide(B.k) * 1000;
      const along = (l, v) => (HORIZ[l] ? ox + B.xm[v] : oz + B.zm[v]);
      for (const e of es) {
        const fS = 256 << e.fk;
        for (let s = 0; s < 4; s++) {
          const [dx, dy] = SIDE_D[s];
          const nb = coverOf(fp, e.fk, e.fx + dx, e.fy + dy, top);
          if (!nb || nb.n.k <= e.n.k) continue;
          const horiz = s < 2;
          const at = sampMm(horiz ? (e.fy + (s === 1 ? 1 : 0)) * fS : (e.fx + (s === 3 ? 1 : 0)) * fS);
          const a = sampMm((horiz ? e.fx : e.fy) * fS), b = sampMm(((horiz ? e.fx : e.fy) + 1) * fS);
          const nk = `${nb.n.k}_${nb.n.tx}_${nb.n.ty}`;
          const l = lineOf(e, s), nl = lineThrough(nb.n, horiz, at);
          adopt.get(key).push({ l, a, b, nk, nl });
          // the coarse node must hold this side's ends and its locked vertices
          const ex = extra.get(nk);
          ex.push({ nl, c: a }, { nl, c: b });
          for (const v of B.lines[l]) {
            const c = along(l, v);
            if (c > a && c < b && B.lock[v]) ex.push({ nl, c });
          }
        }
      }
      // a corner a coarser mesh touches (also only diagonally)
      const n = es[0].n, S = 256 << n.k;
      for (let c = 0; c < 4; c++) {
        const u = n.tx * S + (c & 1 ? S : 0), v = n.ty * S + (c & 2 ? S : 0);
        let C = null;
        for (let L = n.k; L <= top; L++) {
          const cs = 256 << L;
          for (const [ox2, oy2] of [[0, 0], [-1, 0], [0, -1], [-1, -1]]) {
            const cx = Math.floor(u / cs) + ox2, cy = Math.floor(v / cs) + oy2, e = fp.get(`${L}_${cx}_${cy}`);
            if (!e || u < cx * cs || u > (cx + 1) * cs || v < cy * cs || v > (cy + 1) * cs) continue;
            if (e.n.k > n.k && (!C || e.n.k > C.n.k)) C = e;
          }
        }
        if (C) corner.get(key).push({ c, u, v, ck: `${C.n.k}_${C.n.tx}_${C.n.ty}`, C });
      }
    }
  }
  // (2) coarse mesh first; the extras once each, in the order they go in
  const order = [...byNode.keys()].sort((p, q) => byNode.get(q)[0].n.k - byNode.get(p)[0].n.k);
  const exU = new Map();
  for (const key of order) {
    const seen = new Set(), ex = [];
    for (const x of extra.get(key)) { const k2 = x.nl + ':' + x.c; if (!seen.has(k2)) { seen.add(k2); ex.push(x); } }
    exU.set(key, ex);
  }
  return { bases, opt, order, acts, local, adopt, exU, corner, i: 0, works: new Map(), sigs: new Map() };
}
// (3) the next node's Work: a cached one as it was, else stitched now ->
// true while nodes remain
export function stitchStep(P) {
  if (P.i >= P.order.length) return false;
  const key = P.order[P.i++], opt = P.opt, on = opt.stitch !== false, works = P.works;
  // what the coarse meshes hold where this node adopts from them
  const ins = on ? P.adopt.get(key).map(({ l, a, b, nk, nl }) => ({ l, a, b, T: works.get(nk).lineVerts(nl, a, b), holes: works.get(nk).holeSpans(nl, a, b) })) : [];
  const cor = on ? P.corner.get(key).map(({ c, u, v, ck, C }) => {
    const xw = sampMm(u), zw = sampMm(v), [cl, ca] = lineFor(C.n, xw, zw);
    return { c, xw, src: works.get(ck).lineVerts(cl, ca, ca)[0] || null };
  }) : [];
  let str = P.local.get(key);
  if (!opt.twinSig) {
    str += '|x' + P.exU.get(key).map((x) => x.nl + ':' + x.c).join(',');
    for (const { l, a, b, T, holes } of ins) str += `|${l}:${a}:${b}=${T.map((p) => `${p.c},${p.h},${p.d}`).join(';')}/${holes.join(';')}`;
    for (const { c, src } of cor) str += `|c${c}=${src ? src.h + ',' + src.d : '-'}`;
  }
  const sig = hashS(str), hit = opt.cache && opt.cache.get(key);
  P.sigs.set(key, sig);
  if (hit && hit.sig === sig) { works.set(key, hit.work); return P.i < P.order.length; }
  const W = new Work(P.bases.get(key), P.acts.get(key));
  works.set(key, W);
  if (on) {
    for (const { nl, c } of P.exU.get(key)) W.insertAt(nl, c);
    for (const { l, a, b, T, holes } of ins) {
      const Tset = new Set(T.map((p) => p.c));
      for (const p of T) W.insertAt(l, p.c, p.h, p.d);
      // every own vertex there takes the coarse values (a kerb's two copies
      // on the line: the coarse ground has one height, the wall closes)
      const at = new Map(T.map((p) => [p.c, p]));
      W._prep();
      for (const v of W.lines[l]) {
        const p = at.get(W.along(l, v));
        if (p) { W.h[v] = p.h; W.d[v] = p.d; }
      }
      // the own vertices the coarse side lacks: removed, run by run
      // (a vertex inside a coarse hole span stays: it borders the opening)
      const L = W.lineVerts(l, a, b);
      for (const p of L) if (!Tset.has(p.c) && !holes.some(([h0, h1]) => p.c > h0 && p.c < h1)) W.removeRun(l, [p.v]);
      W.edited = true;
    }
    for (const { c, xw, src } of cor) {
      const own = W.lineVerts(c & 2 ? 1 : 0, xw, xw)[0];
      if (!own) continue;                        // the corner is not drawn (a quadrant off)
      if (!src) { W._why('corner'); continue; }
      W.h[own.v] = src.h; W.d[own.v] = src.d;
    }
  }
  return P.i < P.order.length;
}
// -> Map node key -> { work, sig } (opt.cache: replaced by it)
export function stitchDone(P) {
  const res = new Map();
  for (const [key, W] of P.works) res.set(key, { work: W, sig: P.sigs.get(key) });
  const cache = P.opt.cache;
  if (cache) { cache.clear(); for (const [key, r] of res) cache.set(key, r); }
  return res;
}
